const SITE_NAME = "Australian Alumni Association of Cambodia";
const SITE_URL = (process.env.VUE_APP_SITE_URL || "https://www.aaa-c.org").replace(/\/+$/, "");
const DEFAULT_TITLE = `${SITE_NAME} - AAA-C`;
const DEFAULT_DESCRIPTION =
  "AAA-C connects Australian alumni in Cambodia through membership, news, events, opportunities, and community programs.";
const DEFAULT_IMAGE = `${SITE_URL}/aaac-logo.jpg`;

function ensureMeta(attribute, key) {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  return element;
}

function setMetaName(name, content) {
  ensureMeta("name", name).setAttribute("content", content || "");
}

function setMetaProperty(property, content) {
  ensureMeta("property", property).setAttribute("content", content || "");
}

function setCanonical(url) {
  let element = document.head.querySelector('link[rel="canonical"]');

  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "canonical");
    document.head.appendChild(element);
  }

  element.setAttribute("href", url);
}

function setJsonLd(id, data) {
  let element = document.getElementById(id);

  if (!data) {
    if (element) {
      element.remove();
    }
    return;
  }

  if (!element) {
    element = document.createElement("script");
    element.id = id;
    element.type = "application/ld+json";
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify(data);
}

function absoluteUrl(url) {
  if (!url) {
    return DEFAULT_IMAGE;
  }

  try {
    return new URL(url, SITE_URL).toString();
  } catch {
    return DEFAULT_IMAGE;
  }
}

function canonicalFromRoute(route) {
  const canonical = route?.meta?.canonical;
  const canonicalPath = typeof canonical === "function" ? canonical(route) : canonical;
  const routePath = route?.path || "/";
  return absoluteUrl(canonicalPath || routePath);
}

export function stripHtml(value = "") {
  return String(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\s+/g, " ")
    .trim();
}

export function excerpt(value = "", length = 160) {
  const text = stripHtml(value);

  if (text.length <= length) {
    return text || DEFAULT_DESCRIPTION;
  }

  return `${text.slice(0, length - 1).trim()}...`;
}

export function applySeo({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  url = SITE_URL,
  image = DEFAULT_IMAGE,
  type = "website",
  robots = "index,follow",
  jsonLd = null,
} = {}) {
  const finalTitle = title || DEFAULT_TITLE;
  const finalDescription = description || DEFAULT_DESCRIPTION;
  const finalUrl = absoluteUrl(url);
  const finalImage = absoluteUrl(image);

  document.title = finalTitle;
  document.documentElement.lang = "en";

  setMetaName("description", finalDescription);
  setMetaName("robots", robots);
  setCanonical(finalUrl);

  setMetaProperty("og:site_name", SITE_NAME);
  setMetaProperty("og:url", finalUrl);
  setMetaProperty("og:type", type);
  setMetaProperty("og:title", finalTitle);
  setMetaProperty("og:description", finalDescription);
  setMetaProperty("og:image", finalImage);

  setMetaName("twitter:card", "summary_large_image");
  setMetaName("twitter:title", finalTitle);
  setMetaName("twitter:description", finalDescription);
  setMetaName("twitter:image", finalImage);

  setJsonLd("page-json-ld", jsonLd);
}

export function applyRouteSeo(route) {
  applySeo({
    title: route?.meta?.title || DEFAULT_TITLE,
    description: route?.meta?.description || DEFAULT_DESCRIPTION,
    url: canonicalFromRoute(route),
    image: route?.meta?.image || DEFAULT_IMAGE,
    robots: route?.meta?.robots || "index,follow",
  });
}

export function applyContentSeo(content, route) {
  if (!content) {
    applyRouteSeo(route);
    return;
  }

  const title = content.title ? `${content.title} - AAA-C` : route?.meta?.title;
  const description = excerpt(content.content || content.description);
  const url = canonicalFromRoute(route);
  const image = content.thumbnail_image || DEFAULT_IMAGE;

  applySeo({
    title,
    description,
    url,
    image,
    type: "article",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: content.title,
      description,
      image: [absoluteUrl(image)],
      datePublished: content.created_at,
      dateModified: content.updated_at || content.created_at,
      mainEntityOfPage: url,
      publisher: {
        "@type": "Organization",
        name: SITE_NAME,
        logo: {
          "@type": "ImageObject",
          url: DEFAULT_IMAGE,
        },
      },
    },
  });
}
